import { CustomerOrderOfferView } from "src/api/generated/erp/db/types/tables/customerOrderOfferView.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import { OpenCustomerButton } from "src/components/views/erp/utilComponents/OpenCustomerButton.tsx";
import i18n from "i18next";
import {
	faBan,
	faCopy,
	faEnvelope,
	faFileContract,
	faFileExport,
	faHandshake,
	faPrint,
	faShare,
	faSignature,
	faThumbsDown,
	faTimesCircle,
	faUndo,
} from "@fortawesome/pro-regular-svg-icons";
import { OpenCustomerOrderButton } from "src/components/views/erp/utilComponents/OpenCustomerOrderButton.tsx";
import { OpenSalesLeadButton } from "src/components/views/erp/utilComponents/OpenSalesLeadButton.tsx";
import { CustomerOrderOfferActionsApi } from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferActionsApi.ts";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { CreateOrderFromOfferForm } from "src/components/views/erp/sales/customerOrderOffer/CreateOrderFromOfferForm.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { CopyCustomerOrderOfferForm } from "src/components/views/erp/sales/customerOrderOffer/CopyCustomerOrderOfferForm.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { CustomerOfferContractPrintoutApi } from "src/api/generated/erp/sales/customerOrderOffer/contracts/api/customerOfferContractPrintoutApi.ts";
import { CustomerOfferContractSigningApi } from "src/api/generated/erp/sales/customerOrderOffer/contracts/api/customerOfferContractSigningApi.ts";
import { useAavoToast } from "src/components/common/toast/AavoToastContext.tsx";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { SendCustomerOfferContractForSigningForm } from "src/components/views/erp/sales/customerOrderOffer/SendCustomerOfferContractForSigningForm.tsx";
import { useMessageDialog } from "src/components/common/dialogs/messageDialog/MessageDialogContext.tsx";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView";
import { SendObjectDocumentsView } from "src/components/views/tenantMail/SendObjectDocumentsView.tsx";
import { AavoMenuDivider } from "src/components/common/menu/AavoMenuDivider.tsx";
import {
	canCancelCustomerOrderOffer,
	canCreateCustomerOrderFromOffer,
	canEditCustomerOrderOffer,
} from "./customerOrderOfferStateGuards.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { CustomUiActionMenuItems } from "src/components/common/customUiActions/CustomUiActionMenuItems.tsx";
import { PriceRecalculationActions } from "src/components/views/erp/sales/pricing/PriceRecalculationActions.tsx";
import { shouldShowPriceRecalculationActions } from "src/components/views/erp/sales/pricing/priceRecalculationActionVisibility.ts";
import { CustomerOrderOfferProfitMarginApi } from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferProfitMarginApi.ts";

export interface CustomerOrderOfferDataGridRowContextMenuProps {
	row: CustomerOrderOfferView;
	allSelectedRows: CustomerOrderOfferView[];
	refreshData: () => Promise<unknown>;
	offerLinesRefreshRef: RefreshableElementRef;
}

export const CustomerOrderOfferDataGridRowContextMenu = (props: CustomerOrderOfferDataGridRowContextMenuProps) => {
	const { row, allSelectedRows, refreshData, offerLinesRefreshRef } = props;

	const onlySingleRowSelected = allSelectedRows.length === 1;

	const showConfirmDialog = useConfirmDialog();
	const { openDialog } = useGenericDialog();
	const showInputDialog = useInputDialog();
	const tenantCustomizations = useTenantCustomizations();
	const { tenantConfig } = tenantCustomizations;
	const { showToast } = useAavoToast();
	const showMessageDialog = useMessageDialog();
	const hasPermission = useHasPermission();
	return [
		<CustomUiActionMenuItems
			key={"customFunctionButtons"}
			view={"CUSTOMER_OFFERS"}
			contextItems={allSelectedRows.map((offer) => ({
				type: "OBJECT",
				objectId: offer.customerOrderOfferId,
			}))}
		/>,
		onlySingleRowSelected && row.customerOrderOfferState === "INITIAL" && (
			<AsyncMenuButton
				key={"release"}
				label={i18n.t("release")}
				icon={faShare}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						message:
							row.priceRecalculationStatus === "RECALCULATION_REQUIRED" ?
								i18n.t("release_offer_price_recalculation_required_confirmation")
							: i18n.t("release_offer_confirmation"),
					});
					if (!confirmed) return;
					await CustomerOrderOfferActionsApi.releaseCustomerOrderOffer({
						customerOrderOfferId: row.customerOrderOfferId,
					});
					await refreshData();
				}}
			/>
		),
		onlySingleRowSelected && row.customerOrderOfferState === "OFFERED" && (
			<AsyncMenuButton
				key={"revertRelease"}
				label={i18n.t("revert_release")}
				icon={faUndo}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						message: i18n.t("confirm_revert_release_offer"),
					});
					if (!confirmed) return;
					await CustomerOrderOfferActionsApi.revertReleaseCustomerOrderOffer({
						customerOrderOfferId: row.customerOrderOfferId,
					});
					await refreshData();
				}}
			/>
		),
		onlySingleRowSelected && row.customerOrderOfferState === "INITIAL" && (
			<AsyncMenuButton
				key={"discard"}
				label={i18n.t("discard")}
				icon={faTimesCircle}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						message: i18n.t("discard_offer_confirmation"),
					});
					if (!confirmed) return;
					await CustomerOrderOfferActionsApi.discardCustomerOrderOffer({
						customerOrderOfferId: row.customerOrderOfferId,
					});
					await refreshData();
				}}
			/>
		),
		onlySingleRowSelected && canCreateCustomerOrderFromOffer(row.customerOrderOfferState) && (
			<AsyncMenuButton
				key={"createCustomerOrder"}
				label={i18n.t("create_customer_order")}
				icon={faHandshake}
				onClick={() => {
					openDialog(({ closeDialog }) => ({
						title: i18n.t("create_customer_order"),
						size: "sm",
						agentContext: {
							key: defineAgentUiContextKey("erp.sales.customerOffer.createOrderDialog"),
							pathSegments: [i18n.t("create_customer_order")],
							data: {
								customerOrderOfferId: row.customerOrderOfferId,
								customerId: row.customerId,
							},
						},
						content: (
							<CreateOrderFromOfferForm
								customerOrderOfferId={row.customerOrderOfferId}
								onCompleted={async (result) => {
									await closeDialog();
									if (result.type === "success")
										await Promise.all([refreshData(), offerLinesRefreshRef.refresh()]);
								}}
							/>
						),
					}));
				}}
			/>
		),
		onlySingleRowSelected && canCreateCustomerOrderFromOffer(row.customerOrderOfferState) && (
			<AsyncMenuButton
				key={"markAsLost"}
				label={i18n.t("mark_as_lost")}
				icon={faThumbsDown}
				onClick={async () => {
					const loseReason = await showInputDialog({
						type: "string",
						title: i18n.t("lose_reason"),
						fieldLabel: i18n.t("lose_reason"),
						defaultValue: "",
						fieldProps: {
							text: {
								multiLine: true,
							},
						},
					});
					if (loseReason === undefined) return;

					await CustomerOrderOfferActionsApi.markCustomerOrderOfferToLost({
						customerOrderOfferId: row.customerOrderOfferId,
						loseReason: loseReason,
					});
					await refreshData();
				}}
			/>
		),
		onlySingleRowSelected && canCancelCustomerOrderOffer(row.customerOrderOfferState) && (
			<AsyncMenuButton
				key={"cancel"}
				label={i18n.t("cancel")}
				icon={faBan}
				onClick={async () => {
					const confirmed = await showConfirmDialog({
						message: i18n.t("cancel_offer_confirmation"),
					});
					if (!confirmed) return;
					await CustomerOrderOfferActionsApi.cancelCustomerOrderOffer({
						customerOrderOfferId: row.customerOrderOfferId,
					});
					await refreshData();
				}}
			/>
		),
		onlySingleRowSelected &&
			hasPermission(Permissions.Sales.CustomerOffers.MANAGE) &&
			canEditCustomerOrderOffer(row.customerOrderOfferState) &&
			shouldShowPriceRecalculationActions(
				row.customerPriceGroupPricingMethod,
				row.priceRecalculationStatus,
			) && (
				<PriceRecalculationActions
					key={"priceRecalculation"}
					status={row.priceRecalculationStatus}
					recalculatePrices={() =>
						CustomerOrderOfferProfitMarginApi.recalculatePrices({
							customerOrderOfferId: row.customerOrderOfferId,
						})
					}
					acknowledgePriceRecalculation={() =>
						CustomerOrderOfferProfitMarginApi.acknowledgePriceRecalculation({
							customerOrderOfferId: row.customerOrderOfferId,
						})
					}
					onStatusChanged={async () => {
						await Promise.all([refreshData(), offerLinesRefreshRef.refresh()]);
					}}
				/>
			),
		<AavoMenuDivider key={"divider1"} />,
		onlySingleRowSelected && (
			<AsyncMenuButton
				key={"print"}
				label={i18n.t("print_offer")}
				icon={faPrint}
				onClick={async () => {
					const fileHandle = await CustomerOrderOfferActionsApi.printCustomerOrderOffer({
						customerOrderOfferId: row.customerOrderOfferId,
					});
					downloadFile(fileHandle);
				}}
			/>
		),
		onlySingleRowSelected && tenantConfig.erp.customerOfferContractPrintoutEnabled && (
			<AsyncMenuButton
				key={"printContract"}
				label={i18n.t("print_contract")}
				icon={faFileContract}
				onClick={async () => {
					const fileHandle = await CustomerOfferContractPrintoutApi.getCustomerOfferContractPrintout({
						customerOfferId: row.customerOrderOfferId,
					});
					downloadFile(fileHandle);
				}}
			/>
		),
		<AavoMenuDivider key={"divider2"} />,
		onlySingleRowSelected && tenantConfig.documentsEnabled && (
			<AsyncMenuButton
				key={"sendDocuments"}
				label={i18n.t("send_documents")}
				icon={faEnvelope}
				onClick={async () => {
					openDialog(({ closeDialog }) => ({
						title: i18n.t("send_documents"),
						size: "lg",
						agentContext: {
							key: defineAgentUiContextKey("erp.sales.customerOfferDocuments.sendDialog"),
							pathSegments: [i18n.t("send_documents")],
							data: {
								customerOrderOfferId: row.customerOrderOfferId,
								customerId: row.customerId,
							},
						},
						content: (
							<SendObjectDocumentsView
								objectType={"CUSTOMER_ORDER_OFFER"}
								objectId={row.customerOrderOfferId}
								onCompletedSuccessfully={closeDialog}
							/>
						),
					}));
				}}
			/>
		),
		onlySingleRowSelected &&
			tenantConfig.erp.customerOfferContractSigningEnabled &&
			row.customerOrderOfferState === "OFFERED" && (
				<AsyncMenuButton
					key={"sendContractForSigning"}
					label={i18n.t("send_contract_for_signing")}
					icon={faSignature}
					disabled={
						row.externalContractId != null ?
							i18n.t("contract_has_already_been_sent_for_signing")
						:	undefined
					}
					onClick={() => {
						openFormOnDialog({
							openDialog,
							component: SendCustomerOfferContractForSigningForm,
							title: i18n.t("send_contract_for_signing"),
							size: "sm",
							agentContext: {
								key: defineAgentUiContextKey("erp.sales.customerOfferContractSigning.formDialog"),
								data: {
									customerOrderOfferId: row.customerOrderOfferId,
									customerId: row.customerId,
								},
							},
							props: {
								customerOfferId: row.customerOrderOfferId,
							},
							confirmCloseIfEdited: false,
							onSubmit: async () => {
								showToast({
									message: i18n.t("contract_sent_for_signing"),
								});
								await refreshData();
							},
						});
					}}
				/>
			),
		onlySingleRowSelected &&
			tenantConfig.erp.customerOfferContractSigningEnabled &&
			row.externalContractId != null && (
				<AsyncMenuButton
					key={"downloadSignedContract"}
					label={i18n.t("download_signed_contract")}
					icon={faFileContract}
					onClick={tryDownloadSignedContract}
				/>
			),
		onlySingleRowSelected &&
			tenantConfig.erp.customerOfferContractSigningEnabled &&
			row.externalContractId != null && (
				<AsyncMenuButton
					key={"deleteContract"}
					label={i18n.t("delete_contract")}
					icon={faTimesCircle}
					onClick={async () => {
						const confirmed = await showConfirmDialog({
							message: i18n.t("delete_contract_confirm"),
						});
						if (!confirmed) return;
						await CustomerOfferContractSigningApi.deleteContractDocument({
							customerOfferId: row.customerOrderOfferId,
						});
						showToast({
							message: i18n.t("contract_deleted"),
						});
						await refreshData();
					}}
				/>
			),
		<AavoMenuDivider key={"divider3"} />,
		onlySingleRowSelected && (
			<AsyncMenuButton
				key={"copyOffer"}
				label={i18n.t("copy_offer")}
				icon={faCopy}
				onClick={() => {
					openDialog(({ closeDialog }) => ({
						title: i18n.t("copy_offer"),
						size: "sm",
						agentContext: {
							key: defineAgentUiContextKey("erp.sales.customerOffer.copyDialog"),
							pathSegments: [i18n.t("copy_offer")],
							data: {
								sourceCustomerOrderOfferId: row.customerOrderOfferId,
								customerId: row.customerId,
							},
						},
						content: (
							<CopyCustomerOrderOfferForm
								sourceOffer={row}
								onCompleted={async () => {
									await closeDialog();
									await refreshData();
								}}
							/>
						),
					}));
				}}
			/>
		),
		onlySingleRowSelected && tenantConfig.documentsEnabled && tenantConfig.erp.configuratorEnabled && (
			<AsyncMenuButton
				key={"copyConfigurationDocuments"}
				label={i18n.t("copy_configuration_documents_to_offer")}
				icon={faFileExport}
				tooltip={i18n.t("copy_configuration_documents_to_offer.tooltip")}
				onClick={async () => {
					await CustomerOrderOfferActionsApi.copyPinnedDocumentsFromConfigurationsToOffer({
						customerOrderOfferId: row.customerOrderOfferId,
					});
					showToast({
						message: i18n.t("documents_copied_successfully"),
					});
				}}
			/>
		),
		<AavoMenuDivider key={"divider4"} />,
		onlySingleRowSelected &&
			row.customerOrderId != null &&
			hasPermission(Permissions.Sales.CustomerOrders.READ) && (
			<OpenCustomerOrderButton key={"openCustomerOrder"} customerOrderId={row.customerOrderId} />
		),
		onlySingleRowSelected &&
			row.salesLeadId != null &&
			tenantConfig.erp.crmEnabled &&
			hasPermission(Permissions.Sales.SalesLeads.READ) && (
			<OpenSalesLeadButton key={"openSalesLead"} salesLeadId={row.salesLeadId} />
		),
		onlySingleRowSelected && <OpenCustomerButton key={"openCustomer"} customerId={row.customerId} />,
		onlySingleRowSelected && (
			<OpenObjectChangeLogButton
				key={"changeLog"}
				objectRef={{
					objectType: "CUSTOMER_ORDER_OFFER",
					objectId: row.customerOrderOfferId,
				}}
			/>
		),
	];

	async function tryDownloadSignedContract() {
		const result = await CustomerOfferContractSigningApi.tryDownloadAndSaveSignedContractDocument({
			customerOfferId: row.customerOrderOfferId,
		});
		switch (result) {
			case "NOT_YET_SIGNED":
				showMessageDialog({
					content: i18n.t("contract_not_yet_signed"),
				});
				break;
			case "NOT_FOUND":
				showMessageDialog({
					content: i18n.t("contract_not_found"),
				});
				break;
			case "SUCCESS":
				showToast({
					message: i18n.t("signed_contract_downloaded"),
				});
				await refreshData();
				if (tenantConfig.documentsEnabled) {
					openDialog(() => ({
						title: i18n.t("connected_documents"),
						agentContext: {
							key: defineAgentUiContextKey("erp.sales.customerOfferDocuments.dialog"),
							pathSegments: [i18n.t("connected_documents")],
							data: {
								customerOrderOfferId: row.customerOrderOfferId,
								customerId: row.customerId,
							},
						},
						content: (
							<DocumentsOfSingleObjectView
								objectRef={{
									objectType: "CUSTOMER_ORDER_OFFER",
									objectId: row.customerOrderOfferId,
								}}
							/>
						),
					}));
				}
				break;
		}
	}
};
